import axios from 'axios';
import {ProductInfo, TaxInfo} from 'app/models';

const API_URL = process.env.REACT_APP_API_URL;
const ARQUIVEI_URL = `${API_URL}/banks/tax-receipts/arquivei`;

/**
 * O que o serviço devolve ao buscar uma nota pela chave de acesso ou ao
 * converter o XML (as duas respostas têm o mesmo formato). É o corpo de
 * `IncidentItemTaxReceiptInfo` sem `item_general_info`; cada produto traz, além
 * dos campos do modelo, os três valores de ICMS/IPI que o serviço calcula.
 */
export type IncidentItemTaxReceiptInfoResponse = {
  provider_info: {
    social_name: string;
    cnpj: string;
    full_address: string;
    state_tax_number: string;
  };
  tax_receipt_identification: {
    tax_receipt_number: string;
    tax_receipt_serial_number: string;
    access_key: string;
  };
  product_info: Array<
    ProductInfo & {
      icms_value: number;
      icms_aliquot: number;
      ipi_value: number;
    }
  >;
  tax_info: TaxInfo;
};

const JSON_HEADERS = {'Content-Type': 'application/json'};

/**
 * Preenche os dados fiscais pela chave de acesso da nota de origem:
 * `GET /banks/tax-receipts/arquivei/:chave?productCode&completeInformation=false`.
 * Sem chave não chama a API (devolve `undefined`, como o hook de hoje). O
 * código do produto em branco não vai.
 */
export const getArquiveiTaxReceipt = async (
  accessKey: string,
  productCode?: string
): Promise<IncidentItemTaxReceiptInfoResponse | undefined> => {
  if (!accessKey) return undefined;
  const {data} = await axios.get<IncidentItemTaxReceiptInfoResponse>(
    `${ARQUIVEI_URL}/${accessKey}`,
    {
      headers: JSON_HEADERS,
      params: {
        productCode: productCode?.trim() ? productCode : undefined,
        completeInformation: false,
      },
    }
  );
  return data;
};

/**
 * Preenche os dados fiscais pelo XML da nota de origem, já convertido em JSON
 * (`xml2js`): `POST /banks/tax-receipts/arquivei/xml?completeInformation=false`
 * com o JSON em texto. Sem XML não chama a API (devolve `undefined`).
 */
export const postArquiveiXml = async (
  parsedXml: unknown
): Promise<IncidentItemTaxReceiptInfoResponse | undefined> => {
  if (!parsedXml) return undefined;
  const {data} = await axios.post<IncidentItemTaxReceiptInfoResponse>(
    `${ARQUIVEI_URL}/xml`,
    JSON.stringify(parsedXml),
    {
      headers: JSON_HEADERS,
      params: {completeInformation: false},
    }
  );
  return data;
};
